import { config } from '@abyss/web/tools/config';
import find from 'lodash/find';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import {
  Constants,
  MemberCoverageTypeToUICoverageTypeConversion,
  OPTUM_CORRELATION_ID_HEADER,
  UESSearchType,
} from '../../common/Constants';
import { FeatureFlags } from '../../common/ConstantsFeatureFlags';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { getIntValue } from '../../common/Utils/getIntValue';
import { getLanguage } from '../../frontends/ProviderSearch/context/Internationalization/helpers';
import { getPlanSelectorVariables } from '../../frontends/ProviderSearch/context/PlanSelector/helpers';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { usePCPStore } from '../../store/usePCPStore';
import { useVirtualCareStore } from '../../store/useVirtualCareStore';
import { hasNonEmptyEligibilities } from '../../utils/common';
import { returnSuppressFlag } from '../../utils/featureSuppress';
import { getSuppressFacilityFlags } from '../../utils/providerDetails.utils';
import {
  getExcludeNatAncForNetworkIds,
  getLob,
} from '../../utils/providerSearch.utils';
import { removeLeadingZeroes } from '../../utils/string.utils';
import { excludeDisabledResults } from '../../utils/typeahead.utils';
import {
  getClaimOrEligibilitySystemTypeCode,
  getCoverageTypes,
  getDomainForAutoComplete,
  getMemberCoverage,
  getMemberShipCategory,
  getSitusState,
  isCNSMember,
  isDsnpMember,
  isMNRMember,
} from '../../utils/user.utils';
import { useCurrentMemberOrDependentInfo } from '../useCurrentMember';
import { useCustomQuery } from '../useCustomQuery';
import { useFeatureFlag } from '../useFeatureFlag';
import { useGeoLocationStorage } from '../useGeoLocationStorage';
import { useLagoon } from '../useLagoon';
import { useTypeaheadPseudoRollupTable } from '../useTypeaheadPseudoRollup';
import GET_TYPEAHEAD_DETAILS from './AutoComplete.graphql';

enum UesSuggestions {
  EAP = 'EAP',
}

enum PseudoRollupCodeSearchDescription {
  FSF = 'Freestanding facility',
  PLN = 'preferred lab network (PLN)',
  OX_PLN = 'Oxford preferred lab network',
  DDP_RAD = 'Designated Diagnostic Provider: Imaging',
  DDP_LAB = 'Designated Diagnostic Provider: Lab',
}

export const useAutoCompleteQuery = (options) =>
  useCustomQuery(GET_TYPEAHEAD_DETAILS, {
    ...options,
    url: config('GRAPHQL_API_URL'),
    clearCache: [
      {
        key: 'AutoComplete',
        variables: [
          'query',
          'network',
          'latitude',
          'longitude',
          'lang',
          'limit',
          'limitSpl',
          'limitOrg',
          'limitPrn',
          'radius',
        ],
      },
    ],
    accessor: 'autoComplete',
  });

export const useAutoCompleteResults = (options) => {
  const { t } = useTranslation();
  const { dependentSeqNbr } = usePCPStore(
    useShallow((state: any) => ({
      setPCPSearchState: state.setPCPSearchState,
      choosePCP: state.pcpSearchState.choosePCP,
      dependentSeqNbr: state.pcpSearchState.dependentSeqNbr,
    }))
  );

  const currentMember = useCurrentMemberOrDependentInfo(dependentSeqNbr);

  const memberLob = currentMember?.lineOfBusiness ?? null;
  const isCNS = isCNSMember(memberLob);
  const isMNR = isMNRMember(memberLob);
  const isDSNP = isDsnpMember(currentMember);
  const isMNRAndDSNP = isMNR && isDSNP;
  const shouldIncludeHCBSSuggestion = isCNS || isMNRAndDSNP;

  const memberCoverages = getCoverageTypes(currentMember);
  const convertedMemberCoverages = memberCoverages?.map(
    (cov) => MemberCoverageTypeToUICoverageTypeConversion[cov]
  );

  const [
    useMedicalNewRollUpCodes,
    useBHNewRollUpCodes,
    useVisionNewRollUpCodes,
    useDentalNewRollUpCodes,
    useAutocompleteRollUpCodeNameSearch,
    useBHVirtualCarePseudoRollupCodeSearch,
    useOxfordPlnFlag,
    useEnableBhFacilitiesProgramSearchFlag,
    hideDisabledTypeahead,
    enablePlatinumProgramSearch,
    includeAoeOnlySuggestions,
    includeHCBSSuggestion,
    enableAoeMedical,
    isPhonenumberEnabled,
    isNpiEnabled,
    isLicenseEnabled,
    enableVirtualCareProvidersNameSearch,
    isNetworkAffiliationEnabled,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.USE_MEDICAL_NEW_ROLLUP_CODES,
    ConstantsLagoon.FEATURE_FLAGS.USE_BH_NEW_ROLLUP_CODES,
    ConstantsLagoon.FEATURE_FLAGS.USE_VISION_NEW_ROLLUP_CODES,
    ConstantsLagoon.FEATURE_FLAGS.USE_DENTAL_NEW_ROLLUP_CODES,
    ConstantsLagoon.FEATURE_FLAGS.USE_UES_ROLLUP_CODE_FOR_NAME_SEARCH,
    ConstantsLagoon.FEATURE_FLAGS.BEHAVIORAL_HEALTH_VIRTUAL_CARE,
    ConstantsLagoon.FEATURE_FLAGS.OXFORD_PLN,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_FACILITIES_PROGRAM_SEARCH,
    FeatureFlags.FEATURE_FLAG_KEY.HIDE_DISABLED_TYPEAHEAD,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PLATINUM_PROGRAM_SEARCH,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BH_AOE_TYPEAHEAD,
    ConstantsLagoon.FEATURE_FLAGS.SHOW_HCBS_TYPEAHEAD_SUGGESTION,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_AOE_MEDICAL,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PHONE_NUMBER_SEARCH,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_NPI_SEARCH,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LICENSE_SEARCH,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIRTUAL_CARE_PROVIDERS_NAME_SEARCH,
    ConstantsLagoon.FEATURE_FLAGS.ENABLED_NETWORK_AFFILIATION,
  ]);
  const featureSuppression = useLagoon(
    Constants.LAGOON_TABLE.FEATURE_SUPPRESSION
  )();
  const suppressedAOECodesActive = find(featureSuppression, {
    DNT_feature:
      ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.GENDER_AFFIRMING_AOE_CODES,
  })?.active;
  const includeHCBSTypeAheadSuggestion =
    includeHCBSSuggestion && shouldIncludeHCBSSuggestion;

  const featureFlags = useLagoon(Constants.LAGOON_TABLE.FEATURE_FLAGS)();
  const allConfigss = useLagoon('config')();
  const excludeNatAncForNetworkIds = allConfigss?.find(
    (row) => row.key === 'EXCLUDE_NAT_ANC_FOR_NETWORK_IDS'
  )?.value;

  const enableVirtualCareNameSearchPsxLogic = allConfigss?.find(
    (row) => row.key === 'ENABLE_VIRTUAL_CARE_NAME_SEARCH_PSX_LOGIC'
  )?.value;

  const uiMessaging = useLagoon(Constants.LAGOON_TABLE.UI_MESSAGING)();
  const { eligibilities } = useVirtualCareStore((state) => state);
  const { planToken } = getPlanSelectorVariables();
  const planEligibilities = eligibilities[planToken] || [];

  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { coverageType } = chipStore;

  const storedLocation = useGeoLocationStorage();
  const { longitude, latitude } = storedLocation;
  // As per US7088957, do not pass NatAncCommerical network to UES and PES calls for OXFORD members.
  const excludeNatAnc =
    excludeNatAncForNetworkIds?.includes(currentMember?.reciprocityId) ||
    currentMember.membershipCategory === 'OXFORD';
  const network = currentMember
    ? getMemberCoverage(currentMember, featureFlags, excludeNatAnc)
    : '';
  const language = getLanguage()?.code || 'en';
  const typeaheadParams = useLagoon(Constants.LAGOON_TABLE.TYPEAHEAD_PARAMS)();
  const membershipCategory = getMemberShipCategory(currentMember, featureFlags);

  const limitSpl = getIntValue(
    typeaheadParams,
    Constants.TYPEAHEAD_PARAMS.LIMIT_SPECIALTIES,
    Constants.TYPEAHEAD_PARAMS.LIMIT_SPECIALTIES_DEFAULT
  );
  const limitOrg = getIntValue(
    typeaheadParams,
    Constants.TYPEAHEAD_PARAMS.LIMIT_ORG,
    Constants.TYPEAHEAD_PARAMS.LIMIT_ORG_DEFAULT
  );
  const limitPrn = getIntValue(
    typeaheadParams,
    Constants.TYPEAHEAD_PARAMS.LIMIT_PRACTITIONER,
    Constants.TYPEAHEAD_PARAMS.LIMIT_PRACTITIONER_DEFAULT
  );
  const radius = getIntValue(
    typeaheadParams,
    Constants.TYPEAHEAD_PARAMS.RADIUS,
    Constants.TYPEAHEAD_PARAMS.RADIUS_DEFAULT
  );

  const { claimSystemTypeCode, eligibilitySystemTypeCode } =
    getClaimOrEligibilitySystemTypeCode(currentMember, coverageType);
  const pseudoRollupCodeTable = useTypeaheadPseudoRollupTable();

  const {
    suppressPreferredFacilityPLN,
    suppressPreferredFacilityDDP,
    suppressPreferredFacilityFSF,
  } = getSuppressFacilityFlags();

  const suppressEAPKeyword = returnSuppressFlag(
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.EAP
  );

  const pseudoRollupCodeMap = new Map(
    pseudoRollupCodeTable.map(({ dntDescription, pseudoRollupCode }) => [
      dntDescription,
      pseudoRollupCode,
    ])
  );

  const keywordSuppressionFlags = [
    {
      pseudoVertical: pseudoRollupCodeMap.get(
        PseudoRollupCodeSearchDescription.PLN
      ),
      value: suppressPreferredFacilityPLN,
    },
    {
      pseudoVertical: pseudoRollupCodeMap.get(
        PseudoRollupCodeSearchDescription.OX_PLN
      ),
      value: suppressPreferredFacilityPLN,
    },
    {
      pseudoVertical: pseudoRollupCodeMap.get(
        PseudoRollupCodeSearchDescription.FSF
      ),
      value: suppressPreferredFacilityFSF,
    },
    {
      pseudoVertical: pseudoRollupCodeMap.get(
        PseudoRollupCodeSearchDescription.DDP_LAB
      ),
      value: suppressPreferredFacilityDDP,
    },
    {
      pseudoVertical: pseudoRollupCodeMap.get(
        PseudoRollupCodeSearchDescription.DDP_RAD
      ),
      value: suppressPreferredFacilityDDP,
    },
    {
      suggestion: UesSuggestions.EAP,
      value: suppressEAPKeyword,
    },
  ];

  const variables = {
    network,
    latitude,
    longitude,
    lang: language,
    limitSpl,
    limitOrg,
    limitPrn,
    radius,
    membershipCategory,
    claimSystemTypeCode,
    eligibilitySystemTypeCode,
    pseudoRollupCodeTable,
    keywordSuppressionFlags,
    useMedicalNewRollUpCodes,
    useBHNewRollUpCodes,
    useVisionNewRollUpCodes,
    useDentalNewRollUpCodes,
    useAutocompleteRollUpCodeNameSearch,
    useBHVirtualCarePseudoRollupCodeSearch,
    useOxfordPlnFlag,
    useEnableBhFacilitiesProgramSearchFlag,
    enablePlatinumProgramSearch,
    includeAoeOnlySuggestions,
    includeHCBSTypeAheadSuggestion,
    excludeNatAncForNetworkIds: getExcludeNatAncForNetworkIds(
      excludeNatAncForNetworkIds
    ),
    domain: getDomainForAutoComplete(currentMember),
    situsState: getSitusState(currentMember, getLob(memberLob, chipStore?.lob)),
    enableAoeMedical,
    isIFPEarlyPay: currentMember?.isIFPEarlyPay || false,
    isPhonenumberEnabled: isPhonenumberEnabled || false,
    isNpiEnabled: isNpiEnabled || false,
    isLicenseEnabled: isLicenseEnabled || false,
    policyId: suppressedAOECodesActive
      ? // Remove leading zeroes from policy number for consistent downstream processing
        removeLeadingZeroes(currentMember?.policyNumber)
      : undefined,
    isVirtualCareEnabled:
      (enableVirtualCareProvidersNameSearch &&
        enableVirtualCareNameSearchPsxLogic !== 'Y' &&
        hasNonEmptyEligibilities(planEligibilities)) ||
      false,
    isNetworkAffiliationEnabled: isNetworkAffiliationEnabled || false,
  };

  const [autoCompleteResults, GetSearchSuggestions] = useAutoCompleteQuery({
    onCompleted: (autoCompleteResults: any) => {
      const requestData = JSON.parse(autoCompleteResults?.config?.data);
      const correlationId =
        autoCompleteResults?.headers[OPTUM_CORRELATION_ID_HEADER];
      let suggestions =
        autoCompleteResults?.data?.autoComplete?.lang_provider?.langProvider;
      const combinedRollupCodes =
        autoCompleteResults?.data?.autoComplete?.lang_provider
          ?.specialityRollUpCodes;
      const aggregatedAoeCodes =
        autoCompleteResults?.data?.autoComplete?.lang_provider?.aoeCodes;
      const bhProgramFacilityAgg =
        autoCompleteResults?.data?.autoComplete?.lang_provider
          ?.bhProgramFacilityAgg;

      // Extract base data from nested structure
      const providersByNameSearch =
        autoCompleteResults?.data?.autoComplete?.practitioners_uhc?.provData ||
        [];
      const facilitiesByNameSearch =
        autoCompleteResults?.data?.autoComplete?.organizations_uhc?.orgData ||
        [];
      // Just getting medical_groups_uhc and facilities_uhc. Technically NOT using. This is for MNR Acq to show the UI layer response when hit via postman/Insomnia
      const medicalGroupsByNameSearch =
        autoCompleteResults?.data?.autoComplete?.medical_groups_uhc?.provData ||
        [];
      const facilitiesOnlyByNameSearch =
        autoCompleteResults?.data?.autoComplete?.facilities_uhc?.orgData || [];

      const providersByPhoneNumberSearch =
        autoCompleteResults?.data?.autoComplete?.practitioners_uhc
          ?.practitioners_phone_uhc?.provData || [];
      const facilitiesByPhoneNumberSearch =
        autoCompleteResults?.data?.autoComplete?.organizations_uhc
          ?.organizations_phone_uhc?.orgData || [];
      const medicalGroupsByPhoneNumberSearch =
        autoCompleteResults?.data?.autoComplete?.medical_groups_uhc
          ?.medical_groups_phone_uhc?.provData || [];
      const facilitiesOnlyByPhoneNumberSearch =
        autoCompleteResults?.data?.autoComplete?.facilities_uhc
          ?.facilities_phone_uhc?.orgData || [];

      const providersByNpiSearch =
        autoCompleteResults?.data?.autoComplete?.practitioners_uhc
          ?.practitioners_npi_uhc?.provData || [];
      const facilitiesByNpiSearch =
        autoCompleteResults?.data?.autoComplete?.organizations_uhc
          ?.organizations_npi_uhc?.orgData || [];
      const medicalGroupsByNpiSearch =
        autoCompleteResults?.data?.autoComplete?.medical_groups_uhc
          ?.medical_groups_npi_uhc?.provData || [];
      const facilitiesOnlyByNpiSearch =
        autoCompleteResults?.data?.autoComplete?.facilities_uhc
          ?.facilities_npi_uhc?.orgData || [];

      const providersByLicenseSearch =
        autoCompleteResults?.data?.autoComplete?.practitioners_uhc
          ?.practitioners_lic_uhc?.provData || [];
      const facilitiesByLicenseSearch =
        autoCompleteResults?.data?.autoComplete?.organizations_uhc
          ?.organizations_lic_uhc?.orgData || [];
      const medicalGroupsByLicenseSearch =
        autoCompleteResults?.data?.autoComplete?.medical_groups_uhc
          ?.medical_groups_lic_uhc?.provData || [];
      const facilitiesOnlyByLicenseSearch =
        autoCompleteResults?.data?.autoComplete?.facilities_uhc
          ?.facilities_lic_uhc?.orgData || [];
      const filterData = autoCompleteResults?.data?.autoComplete;

      if (hideDisabledTypeahead) {
        suggestions = excludeDisabledResults(
          convertedMemberCoverages,
          suggestions
        );
      }
      const typeaheadSearchHeader = !!currentMember?.isIFPEarlyPay
        ? find(uiMessaging, {
            key: ConstantsLagoon.UI_MESSAGING
              .TYPEAHEAD_SEARCH_HEADER_IFP_EARLYPAY,
          })?.message
        : t('Provider, facilities and provider groups');
      const virtualProviders =
        autoCompleteResults?.data?.autoComplete?.virtual_providers_uhc || [];
      const query = requestData?.variables?.query;
      const has10DigitSearch = query ? /^\d{10}$/.test(query) : false;

      const typeAheadSuggestions = [
        ...(suggestions?.map((item) => ({
          ...item,
          showSearchIcon: true,
        })) || []),
        ...(network
          ? [
              {
                section: typeaheadSearchHeader,
                uesSearchType: UESSearchType.Name,
                items: [
                  ...providersByNameSearch,
                  ...facilitiesByNameSearch,
                ].map((item) => ({ ...item, showSearchIcon: false })),
              },
              ...(has10DigitSearch
                ? [
                    {
                      section: t('facilities-phone'),
                      uesSearchType: UESSearchType.PhoneNumber,
                      items: facilitiesByPhoneNumberSearch
                        .filter((item) => item.providerType === 'Facility')
                        .map((item) => ({
                          ...item,
                          showSearchIcon: false,
                        })),
                    },
                    {
                      section: t('medical-groups-phone'),
                      uesSearchType: UESSearchType.PhoneNumber,
                      items: facilitiesByPhoneNumberSearch
                        .filter(
                          (item) => item.providerType === 'Provider Group'
                        )
                        .map((item) => ({
                          ...item,
                          showSearchIcon: false,
                        })),
                    },
                    {
                      section: t('providers-phone'),
                      uesSearchType: UESSearchType.PhoneNumber,
                      items: providersByPhoneNumberSearch.map((item) => ({
                        ...item,
                        showSearchIcon: false,
                      })),
                    },
                    {
                      section: t('providers-npi'),
                      uesSearchType: UESSearchType.NpiNumber,
                      items: providersByNpiSearch.map((item) => ({
                        ...item,
                        showSearchIcon: false,
                      })),
                    },
                    {
                      section: t('medical-groups-npi'),
                      uesSearchType: UESSearchType.NpiNumber,
                      items: facilitiesByNpiSearch
                        .filter(
                          (item) => item.providerType === 'Provider Group'
                        )
                        .map((item) => ({
                          ...item,
                          showSearchIcon: false,
                        })),
                    },
                    {
                      section: t('facilities-npi'),
                      uesSearchType: UESSearchType.NpiNumber,
                      items: facilitiesByNpiSearch
                        .filter((item) => item.providerType === 'Facility')
                        .map((item) => ({
                          ...item,
                          showSearchIcon: false,
                        })),
                    },
                  ]
                : []),
              {
                section: t('providers-license'),
                uesSearchType: UESSearchType.LicenseNumber,
                items: providersByLicenseSearch.map((item) => ({
                  ...item,
                  showSearchIcon: false,
                })),
              },
              {
                section: t('medical-groups-license'),
                uesSearchType: UESSearchType.LicenseNumber,
                items: facilitiesByLicenseSearch
                  .filter((item) => item.providerType === 'Provider Group')
                  .map((item) => ({
                    ...item,
                    showSearchIcon: false,
                  })),
              },
              {
                section: t('facilities-license'),
                uesSearchType: UESSearchType.LicenseNumber,
                items: facilitiesByLicenseSearch
                  .filter((item) => item.providerType === 'Facility')
                  .map((item) => ({
                    ...item,
                    showSearchIcon: false,
                  })),
              },
            ].filter((section) => section.items.length)
          : []),
        ...(enableVirtualCareProvidersNameSearch
          ? [{ heading: 'Virtual Providers', items: virtualProviders }]
          : []),
      ];

      options.onCompleted({
        suggestions,
        typeAheadSuggestions,
        combinedRollupCodes,
        aggregatedAoeCodes,
        bhProgramFacilityAgg,
        requestData,
        correlationId,
        providers: [
          ...providersByNameSearch,
          ...providersByPhoneNumberSearch,
          ...providersByNpiSearch,
          ...providersByLicenseSearch,
        ],
        facilities: [
          ...facilitiesByNameSearch,
          ...facilitiesByPhoneNumberSearch,
          ...facilitiesByNpiSearch,
          ...facilitiesByLicenseSearch,
        ],
        medicalGroups: [
          ...medicalGroupsByNameSearch,
          ...medicalGroupsByPhoneNumberSearch,
          ...medicalGroupsByNpiSearch,
          ...medicalGroupsByLicenseSearch,
        ],
        facilitiesOnly: [
          ...facilitiesOnlyByNameSearch,
          ...facilitiesOnlyByPhoneNumberSearch,
          ...facilitiesOnlyByNpiSearch,
          ...facilitiesOnlyByLicenseSearch,
        ],
        filterData,
      });
    },
    onError: () => {},
  });

  const getAutoCompleteResults = ({ query }) => {
    GetSearchSuggestions({
      variables: { ...variables, query },
    });
  };

  return [autoCompleteResults, getAutoCompleteResults];
};
